import { useFormContext } from 'react-hook-form';
import { generateInitialValuesForInputs } from 'components/Applications/ApplicationForm/initialValuesGenerator';
import { HookArray } from 'components/UI/HookForm/Form/Array';

import { flatten } from 'utils/flattenObject';
import InputError from 'components/UI/Form/Error';
import {
  JsonInput,
  Section,
} from 'components/UI/JsonForm/JsonInputRenderer/types';
import { inputMap } from 'components/UI/JsonForm/JsonInputRenderer/inputmap';
import { ConditionalComponent } from 'components/UI/JsonForm/JsonInputRenderer/ConditionalComponent';

export interface JsonInputRendererProps {
  section: Section;
  formValues: any;
  currentStep?: number;
}
export const ApplicationFormInputRenderer = (props: JsonInputRendererProps) => {
  const { section, formValues: values, currentStep = 0 } = props;
  const { name: sectionName, inputs } = section;
  const currentSectionRegEx = new RegExp(`${sectionName}.[0-9.]*message`);
  const {
    getValues,
    formState: { errors },
  } = useFormContext();
  const flatErrors = flatten(errors);
  const currentSectionErrors = Object.keys(flatErrors).filter((key) =>
    key.match(currentSectionRegEx)
  );

  const renderInputs = (
    inputs: JsonInput[],
    nameAppendString?: string,
    arrayId?: string
  ) => {
    return inputs.map((input) => {
      const {
        type,
        props,
        gridColSpan = 'col-span-4',
        isArray,
        isDependent,
        arrayAddButtonLabel,
        arrayName,
        minArrayLength,
        maxArrayLength,
        dependentInputName = '',
        dependentInputValue,
        inputs: nestedInput,
      } = input;
      const { Component, defaultProps } = inputMap[type];
      const arrayFieldInfo = nameAppendString?.split('.') || [];
      const getDependentValue = () => {
        if (arrayFieldInfo.length) {
          const dependentValue = arrayFieldInfo.reduce<any>((acc, current) => {
            if (current && acc) {
              acc = acc[current];
            }
            return acc;
          }, values);
          if (dependentValue) {
            // TODO: need to verify for nested objected inside array
            return dependentValue[dependentInputName];
          } else {
            return undefined;
          }
        } else {
          return getValues(dependentInputName);
        }
      };

      const updatedName = `${nameAppendString || ''}${props.name}${
        type === 'file' && arrayId ? `:${arrayId}` : ''
      }`;
      const updatedProps = {
        ...props,
        name: updatedName,
      } as any;

      const dependentValue = getDependentValue();

      const inputComponent = (
        <div
          key={updatedName}
          className={
            isDependent && dependentInputValue !== dependentValue
              ? 'absolute'
              : gridColSpan
          }
        >
          {Component && (
            <ConditionalComponent
              name={updatedName}
              isDependent={isDependent}
              dependentInputValue={dependentInputValue}
              dependentValue={dependentValue}
              getDependentValue={getDependentValue}
            >
              <Component
                {...updatedProps}
                {...defaultProps}
                currentStep={type === 'file' ? currentStep : undefined}
              />
            </ConditionalComponent>
          )}
          {nestedInput &&
            nestedInput.length > 0 &&
            (isArray && arrayName ? (
              <HookArray
                addButtonLabel={arrayAddButtonLabel}
                name={arrayName}
                minArrayLength={minArrayLength}
                defaultValues={generateInitialValuesForInputs(nestedInput)}
                maxArrayLength={maxArrayLength}
              >
                {(fieldName, removeButton, arrayId) => (
                  <div className='grid grid-cols-12 gap-4 w-full h-full content-start'>
                    {renderInputs(nestedInput, fieldName, arrayId)}
                    {removeButton}
                  </div>
                )}
              </HookArray>
            ) : (
              renderInputs(nestedInput)
            ))}
        </div>
      );

      return <>{inputComponent}</>;
    });
  };

  return (
    <>
      {section.title && (
        <div className='col-span-12 space-y-0'>
          <h2 className='mb-1 text-primaryColor-normal'>{section.title}</h2>
          {section.subTitle && (
            <p className='text-secondaryColor-normal'>{section.subTitle}</p>
          )}
        </div>
      )}
      {renderInputs(inputs)}
      {currentSectionErrors?.length > 0 && (
        <div className='col-span-12 space-y-5'>
          {currentSectionErrors?.map((errorKey) => (
            <InputError key={errorKey}>
              {`* ${flatErrors[errorKey as keyof typeof flatErrors]}`}
            </InputError>
          ))}
        </div>
      )}
    </>
  );
};
